import { FC, useEffect, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { ArrowBack } from '@mui/icons-material'
import { Box, Button, Card, CardContent, Checkbox, FormControlLabel, Grid, Skeleton, Stack, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { UserLanguage } from '../../../features/account/types/User'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetInternalAnalystsQuery } from '../../api/analysts'
import { useGetInternalGameDocumentQuery } from '../../api/games'
import { stillStoredError, useDeleteGameReviewMutation, useGetInternalGameReviewQuery, useSaveGameReviewMutation } from '../../api/reviews'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { AnalystPicker } from '../../features/review/components/AnalystPicker/AnalystPicker'
import { PreviewLanguageSelect } from '../../features/review/components/PreviewLanguageSelect/PreviewLanguageSelect'
import { ReviewGameHeader } from '../../features/review/components/ReviewGameHeader/ReviewGameHeader'
import { ReviewPreview } from '../../features/review/components/ReviewPreview/ReviewPreview'
import {
  emptyTranslation,
  gameReviewFieldLabelKeys,
  GameReviewFormValues,
  getEditorLeavePath,
  getGameReviewEditPath,
  getUnstoredGameReviewFields,
  needsPublishConfirmation,
  storedFieldLabelKeys,
  toGameReviewFormValues,
  toGameReviewMutation,
} from '../../features/review/helpers/reviewHelpers'
import { gameReviewFields } from '../../features/review/types/InternalReview'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { useScreenshotLinkEditorConfig } from '../../hooks/useScreenshotLinkEditorConfig'
import { InputTranslatorType } from '../../types/InputTranslatorType'

type PageAction = 'publish' | 'delete'

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const GameReviewEditPage: FC = () => {
  const { appId = '', reviewId } = useParams<{ appId: string; reviewId?: string }>()
  usePage(pageService.getInternalPageWithId('internal-game-review-edit'))
  const { t } = useTranslation()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const analysisId = searchParams.get('analysisId')
  const gameQuery = useGetInternalGameDocumentQuery(appId, { skip: !appId })
  const reviewQuery = useGetInternalGameReviewQuery(reviewId ?? '', { skip: !reviewId })
  const analystsQuery = useGetInternalAnalystsQuery()
  const [saveReview, { isLoading: isSaving }] = useSaveGameReviewMutation()
  const [deleteReview, { isLoading: isDeleting }] = useDeleteGameReviewMutation()
  const [previewLanguage, setPreviewLanguage] = useState<UserLanguage>('en')
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<PageAction>>()
  const {
    control,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm<GameReviewFormValues>({ defaultValues: toGameReviewFormValues(undefined) })
  const values = useWatch({ control }) as GameReviewFormValues
  const { guard, leave } = useEditPageLeave(isDirty)
  const game = gameQuery.data
  const review = reviewId ? reviewQuery.data ?? undefined : undefined
  const editorConfig = useScreenshotLinkEditorConfig(game?.id)
  const loadedReviewId = useRef<string>()

  // Only on the first load of a review, so a refetch can't replace edits that weren't saved.
  useEffect(() => {
    if (review && loadedReviewId.current !== review.id) {
      loadedReviewId.current = review.id
      reset(toGameReviewFormValues(review))
    }
  }, [review, reset])

  const save = async () => {
    if (!game?.appId) return
    const body = toGameReviewMutation(review, game.appId, getValues())
    try {
      const stored = await saveReview(body).unwrap()
      loadedReviewId.current = stored.id
      reset(toGameReviewFormValues(stored))
      const unstored = getUnstoredGameReviewFields(body, stored)
      if (unstored.length) notifyError(t('internal-analysts:review_not_stored', { fields: unstored.map((field) => t(storedFieldLabelKeys[field])).join(', ') }))
      else notifySuccess(t('internal-analysts:review_saved'))
      if (stored.id !== reviewId) leave(getGameReviewEditPath(appId, stored.id, analysisId), { replace: true })
    } catch {
      notifyError(t('internal-analysts:review_save_failed'))
    }
  }

  const remove = async () => {
    if (!review) return
    try {
      await deleteReview(review.id).unwrap()
      notifySuccess(t('internal-analysts:review_deleted'))
      leave(getEditorLeavePath(analysisId))
    } catch (error) {
      const stillStored = (error as { error?: string } | undefined)?.error === stillStoredError
      notifyError(stillStored ? t('internal-analysts:review_still_stored') : t('internal-analysts:review_delete_failed'))
    }
  }

  const handleSave = () => {
    if (!needsPublishConfirmation(review, getValues())) {
      save()
      return
    }
    setConfirmDialogData({
      title: t('internal-analysts:review_publish_title'),
      confirmText: t('internal-analysts:review_publish_confirm', { name: game?.name }),
      actionText: t('internal-analysts:review_publish'),
      data: 'publish',
    })
  }

  const handleDelete = () =>
    setConfirmDialogData({
      title: t('internal-analysts:review_delete_title'),
      confirmText: t('internal-analysts:review_delete_confirm', { name: game?.name }),
      actionText: t('internal-analysts:review_delete'),
      destructiveAction: true,
      data: 'delete',
    })

  const handleConfirm = (confirmed?: ConfirmDialogData<PageAction>) => {
    setConfirmDialogData(undefined)
    if (confirmed?.data === 'publish') save()
    if (confirmed?.data === 'delete') remove()
  }

  const isLoading = gameQuery.isLoading || (!!reviewId && reviewQuery.isLoading)
  const loadError = gameQuery.isError
    ? t('internal-analysts:review_game_load_failed')
    : !!reviewId && reviewQuery.isError
    ? t('internal-analysts:review_load_failed')
    : !!reviewId && reviewQuery.isSuccess && !reviewQuery.data
    ? t('internal-analysts:review_not_found')
    : review && game && review.appId !== game.appId
    ? t('internal-analysts:review_other_game', { appId: review.appId })
    : undefined
  const isBusy = isSaving || isDeleting
  const analyst = analystsQuery.data?.find(({ id }) => id === values.analystId)

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(getEditorLeavePath(analysisId))}>
            {t('internal-common:back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {t('internal-analysts:review_title')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {review && (
            <Button color="error" onClick={handleDelete} disabled={isLoading || !!loadError || isBusy}>
              {t('internal-common:delete')}
            </Button>
          )}
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !!loadError || isBusy || !game?.appId}>
            {t('internal-common:save')}
          </Button>
        </Stack>
      </Stack>

      <QueryStateBoundary isLoading={isLoading} isError={!!loadError} errorText={loadError} skeletonHeight={480}>
        {game && (
          <Grid container spacing={3} wrap="wrap">
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Stack spacing={3}>
                    <ReviewGameHeader game={game} />
                    <Controller
                      name="analystId"
                      control={control}
                      render={({ field }) => <AnalystPicker value={field.value} onChange={(id) => field.onChange(id)} disabled={isBusy} />}
                    />
                    {editorConfig ? (
                      gameReviewFields.map((contentField) => (
                        <Controller
                          key={contentField}
                          name={`content.${contentField}`}
                          control={control}
                          render={({ field }) => (
                            <InputTranslator
                              value={field.value ?? emptyTranslation}
                              onChange={field.onChange}
                              inputType={InputTranslatorType.WYSIWYG}
                              label={t(gameReviewFieldLabelKeys[contentField])}
                              dialogTitle={t(gameReviewFieldLabelKeys[contentField])}
                              wysiwygConfig={editorConfig}
                              disabled={isBusy}
                            />
                          )}
                        />
                      ))
                    ) : (
                      <Skeleton variant="rectangular" height={200} />
                    )}
                    <Box>
                      <Controller
                        name="published"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} disabled={isBusy} />}
                            label={t('internal-analysts:review_published')}
                          />
                        )}
                      />
                      {review?.published && (
                        <Typography variant="body2" color="text.secondary">
                          {t('internal-analysts:review_published_help')}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'sticky', top: (theme) => theme.spacing(2) }}>
                <ReviewPreview
                  title={t('internal-analysts:review_preview_title')}
                  subtitle={t('internal-analysts:review_preview_subtitle')}
                  analyst={analyst}
                  sections={gameReviewFields.map((contentField) => ({
                    key: contentField,
                    heading: t(gameReviewFieldLabelKeys[contentField]),
                    html: values.content?.[contentField]?.[previewLanguage],
                  }))}
                  actions={<PreviewLanguageSelect value={previewLanguage} onChange={setPreviewLanguage} />}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </QueryStateBoundary>

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:review_discard_title')}
        text={t('internal-analysts:review_discard_confirm')}
        discardText={t('internal-analysts:review_discard')}
      />
    </Box>
  )
}
